import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, X } from "lucide-react";
import { storage } from "util/storage";
import { useFeatureFlag } from "hooks/useFeatureFlag";

/** 24 hours in milliseconds */
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Returns the namespaced localStorage key used to track whether the
 * experimental banner for a given feature has been dismissed.
 */
const dismissalKey = (featureId: string): string =>
  `experimentalDismissed:${featureId}`;

interface ExperimentalFeatureBannerProps {
  /**
   * Unique feature slug (e.g. "graph-explorer").
   * Used to look up the feature in `app_settings` and to scope the
   * localStorage dismissal key.
   */
  featureId: string;
}

/**
 * Inline banner that labels a page/feature as experimental and invites
 * the user to provide feedback.  The banner fetches its own data from
 * the `app_settings` API so the consumer only needs to supply the
 * feature slug.
 *
 * Dismissable via an X button; the dismissal is stored in localStorage
 * with a 24-hour TTL (same pattern as FloatingFeedbackButton).
 */
const ExperimentalFeatureBanner: React.FC<ExperimentalFeatureBannerProps> = ({
  featureId,
}) => {
  const [isDismissed, setIsDismissed] = useState<boolean>(true);
  const { feature } = useFeatureFlag(featureId, !isDismissed);

  useEffect(() => {
    const dismissed = storage.getJSONWithTTL<boolean>(
      dismissalKey(featureId),
      false,
    );
    setIsDismissed(dismissed);

  }, [featureId]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    storage.setJSONWithTTL(dismissalKey(featureId), true, DISMISS_TTL_MS);
  }, [featureId]);

  if (isDismissed || !feature) {
    return null;
  }

  return (
    <div className="experimental-feature-banner flex-shrink-0 flex items-center justify-between gap-3 bg-eoe-entity-feedback px-4 py-2 text-sm text-white">
      <div className="experimental-feature-banner-content flex items-center gap-2 min-w-0">
        <FlaskConical className="size-4 flex-shrink-0" />
        <span className="experimental-feature-banner-text">
          Experimental; provide feedback {" "}
          <Link
            to={`/feedback/${feature.feedback_id}`}
            className="experimental-feature-banner-link underline font-semibold hover:opacity-80 transition-opacity"
          >
            here
          </Link>
        </span>
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        className="experimental-feature-banner-close flex-shrink-0 rounded p-0.5 hover:bg-white/20 transition-colors"
        aria-label="Dismiss experimental feature banner"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

export default ExperimentalFeatureBanner;
